"use client";

import { Info } from "lucide-react";
import { formatPrice } from "@/lib/api";
import { fairZone, roundEstimate } from "@/lib/estimate";
import { useT, useLocale } from "@/lib/locale-context";
import { translateVerdict } from "@/lib/i18n";
import HoverTip from "@/components/HoverTip";

interface Props {
  /** Inside a card already (the listing page's verdict section): no
   *  border, tint or padding of its own, so there is no card in a card. */
  flat?: boolean;
  verdict: string;
  overpriced_pct: number;
  price: number;
  predicted_price: number;
  // Conformal band (80% interval by default). When absent we fall back to
  // a visual ±5% spread so older pickles / rent listings don't render an
  // empty "Estimated" column.
  predicted_price_low?: number | null;
  predicted_price_high?: number | null;
  /** The fair zone the verdict tier is made from (backend fair_low /
   *  fair_high): the range the card prints and shades. Derived from the
   *  band with the same arithmetic when a payload predates it. */
  fair_low?: number | null;
  fair_high?: number | null;
  confidence_pct?: number | null;
  savings: number;
  criticality?: number | null;
  // Optional - the ORIGINAL model estimate before the description
  // penalty pipeline shifted the band downward. When passed, a small
  // "Model: X AZN (−25%)" line renders under the Fair Value Range so
  // the user sees both numbers in the same card. We keep the amber
  // adjustment banner separate from this component; this prop just
  // mirrors the summary into the gauge card for quick reference.
  model_estimate_price?: number | null;
  total_adjustment_pct?: number | null;
  /** Analysis mode. Controls the rounding grain used when rendering
   *  the fair-value band: sale listings round to 1000 AZN (clean
   *  "325 000 – 475 000" shape for big numbers); rent listings round
   *  to 10 AZN so the band doesn't collapse to "1 000 – 1 000 AZN"
   *  when the true range is e.g. 509–762 AZN/mo. */
  analysis_mode?: "sale" | "rent";
}

/**
 * Visual gauge showing where a property falls on the deal spectrum.
 * Range: -30% (exceptional deal) to +30% (heavily overpriced).
 * Animated indicator dot with color gradient bar.
 */
export default function VerdictGauge({
  verdict,
  overpriced_pct,
  price,
  predicted_price,
  predicted_price_low,
  predicted_price_high,
  fair_low,
  fair_high,
  confidence_pct,
  savings,
  criticality,
  model_estimate_price,
  total_adjustment_pct,
  analysis_mode = "sale", flat = false }: Props) {
  // Band rounding grain: rent bands live in the 100s of AZN/mo, so
  // rounding to the nearest 1000 (the sale-friendly grain) collapses
  // endpoints like 509/762 to 1000/1000 - both bounds would then
  // render as the same number, making the "Real value range" chip
  // look broken. Rent uses a 10 AZN grain for readable bounds.
  const bandGrain = analysis_mode === "rent" ? 10 : 1000;
  const roundBand = (n: number) => Math.round(n / bandGrain) * bandGrain;
  // Clamp percentage to gauge range
  const pct = Math.max(-35, Math.min(35, overpriced_pct));
  // Map to 0-100 position on the gauge (center = fair price)
  const position = ((pct + 35) / 70) * 100;

  // Band edges in the same percent-vs-predicted space the gauge uses.
  // Falls back to ±5% when the backend didn't return a band so the shaded
  // region + range label still render consistently.
  const hasBand = predicted_price_low != null && predicted_price_high != null && predicted_price > 0;
  const bandLow = hasBand ? predicted_price_low! : predicted_price * 0.95;
  const bandHigh = hasBand ? predicted_price_high! : predicted_price * 1.05;
  // The printed range and the shaded region are the fair zone the tier
  // is made from, so the asking price never sits "inside the range" under
  // an overpriced verdict; the model band stays behind the info mark.
  const zone = fairZone({ predicted_price, fair_low, fair_high, predicted_price_low, predicted_price_high });
  const zoneLow = zone ? zone.low : predicted_price * 0.95;
  const zoneHigh = zone ? zone.high : predicted_price * 1.05;
  const bandLowPct = ((zoneLow - predicted_price) / predicted_price) * 100;
  const bandHighPct = ((zoneHigh - predicted_price) / predicted_price) * 100;
  const bandLeft = Math.max(0, ((Math.max(-35, bandLowPct) + 35) / 70) * 100);
  const bandRight = Math.min(100, ((Math.min(35, bandHighPct) + 35) / 70) * 100);

  const t = useT();
  const { locale } = useLocale();
  const unit = analysis_mode === "rent" ? t("result.permonth") : "AZN";
  const zoneTip = zone
    ? t("result.fairzone.tooltip")
        .replace("{pct}", String(Math.round(zone.half * 100)))
        .replace("{range}", `${formatPrice(roundBand(bandLow))} - ${formatPrice(roundBand(bandHigh))} ${unit}`)
    : "";

  // A fair verdict stays fair in words and colour: the fair zone is up to
  // ±15% of the estimate (analyzer_service.compute_verdict), so "Ədalətli
  // qiymət" over a red "11.5% real dəyərdən yuxarı" contradicted itself
  // (owner, 2026-11-01). Its sub-line says it is inside the fair range and
  // how far from the estimate.
  const isFair = verdict === "Fair Price";
  const isUnderpriced = !isFair && overpriced_pct < -5;
  const isOverpriced = !isFair && overpriced_pct > 5;
  const fairGap = isFair && Math.abs(overpriced_pct) >= 1
    ? t(overpriced_pct > 0 ? "result.fair.above" : "result.fair.below").replace("{pct}", Math.abs(overpriced_pct).toFixed(1))
    : null;

  // Mortgage-transfer listings: the sticker price isn't comparable to
  // the model's prediction. We don't render a green/red card and we
  // don't phrase the sub-line in % terms - everything goes amber so
  // the user sees the top-side MortgageTransferBanner as the source
  // of truth about what they're paying.
  const isNeedsVerification = verdict === "Needs Verification";
  // Luxury Segment: premium complex the model can't price reliably - render
  // like Needs Verification (amber panel, no gauge/band) because the point
  // estimate is known to undershoot and showing it would mislead.
  const isLuxury = verdict === "Luxury Segment";
  // Suspiciously High: premium complex AND asking is 2x+ the prediction.
  // Same amber edge treatment as Luxury Segment because the model still
  // can't price the listing - but the framing is cautionary, not soft
  // ("verify carefully" vs. "model can't estimate this category").
  const isSuspiciousHigh = verdict === "Suspiciously High";

  // When the AI flags serious risks (criticality 0 or 1) on a price that
  // would otherwise read as a great deal, suppress the celebratory framing -
  // the bargain is suspect, not a win. Saves the user from being lured by
  // a green card on a unit with no title deed / structural issues.
  const isSuspiciousBargain =
    isUnderpriced && criticality != null && criticality <= 1;

  const translatedVerdict = isNeedsVerification
    ? t("verdict.needsverification")
    : isSuspiciousBargain
      ? t("verdict.suspiciousbargain")
      : translateVerdict(verdict, locale);

  // Verdict color for the main text
  const verdictColor = isNeedsVerification || isSuspiciousBargain || isLuxury || isSuspiciousHigh
    ? "text-amber-700"
    : isUnderpriced
      ? "text-emerald-600"
      : isOverpriced
        ? "text-rose-600"
        : "text-gray-700";

  const verdictBg = isNeedsVerification || isSuspiciousBargain || isLuxury || isSuspiciousHigh
    ? "bg-amber-50 border-amber-200"
    : isUnderpriced
      ? "bg-emerald-50 border-emerald-200"
      : isOverpriced
        ? "bg-rose-50 border-rose-200"
        : "bg-white border-gray-200";

  return (
    <div className={flat ? "" : `rounded-2xl border p-4 sm:p-6 ${verdictBg}`}>
      {/* Verdict + Percentage. Padding + verdict text both step down
          on mobile: p-6 left a chunk of unused breathing room around
          a verdict label that's already wide in AZ ("Ədalətli qiymət")
          - wide enough that text-2xl wraps to two lines when the
          savings stat sits in the right column. Dropping the heading
          to text-xl + tightening padding to p-4 keeps the verdict on
          a single line and shaves ~24px of vertical space off the
          card on a 390px viewport. */}
      <div className="flex items-center justify-between mb-4 sm:mb-5">
        <div>
          <div className={`text-xl sm:text-2xl font-extrabold ${verdictColor}`}>
            {translatedVerdict}
          </div>
          <div className="text-sm text-gray-500 mt-0.5">
            {isNeedsVerification
              ? t("verdict.needsverification.sub")
              : isLuxury
                ? t("verdict.luxury.sub")
                : isSuspiciousHigh
                  ? t("verdict.suspicioushigh.sub")
                  : isSuspiciousBargain
                    ? t("verdict.suspiciousbargain.sub")
                    : isUnderpriced
                      ? `${Math.abs(overpriced_pct).toFixed(1)}% ${t("result.below")}`
                      : isOverpriced
                        ? `${overpriced_pct.toFixed(1)}% ${t("result.above")}`
                        : fairGap ?? t("result.atfair")}
          </div>
        </div>
        {savings > 0 && !isSuspiciousBargain && !isNeedsVerification && !isLuxury && !isSuspiciousHigh && (
          <div className="text-right shrink-0 ml-3">
            <div className="text-xs text-gray-400 tracking-wide">
              {t("result.savings")}
            </div>
            <div className="text-lg sm:text-xl font-bold text-emerald-600 whitespace-nowrap">
              {formatPrice(savings)} AZN
            </div>
          </div>
        )}
      </div>

      {/* Gauge Bar - suppressed for mortgage-transfer because the listing
          price is incomparable to the model prediction; the banner above
          carries the real cost story instead. */}
      {!isNeedsVerification && !isLuxury && !isSuspiciousHigh && (
      <div className="relative mb-4">
        {/* Background gradient bar */}
        <div className="h-3 rounded-full bg-grad-verdict relative overflow-visible">
          {/* Conformal band - anything between these two edges reads as
              "Fair Price". Using a soft white overlay rather than a tinted
              band keeps the underlying colour gradient legible. */}
          <div
            className="absolute top-0 bottom-0 bg-white/50 ring-1 ring-white/70 rounded-full pointer-events-none"
            style={{ left: `${bandLeft}%`, right: `${100 - bandRight}%` }}
            aria-hidden="true"
          />

          {/* Center marker (fair price) */}
          <div
            className="absolute top-0 bottom-0 w-px bg-gray-500/30"
            style={{ left: "50%" }}
          />

          {/* Indicator: a pin, not a knob (it read as a slider thumb) */}
          <div
            className="pointer-events-none absolute -top-1.5 h-6 w-[3px] rounded-full shadow-[0_0_0_2px_rgba(255,255,255,0.9)] transition-all duration-700 ease-out"
            style={{
              left: `${position}%`,
              transform: "translateX(-50%)",
              backgroundColor: isSuspiciousBargain
                ? "#d97706"
                : isUnderpriced
                  ? "#059669"
                  : isOverpriced
                    ? "#e11d48"
                    : "#6b7280",
            }}
          />
        </div>

        {/* Labels under the gauge: the full analysis's words, one set on
            every surface (the listing page said "Əla Təklif / Normal
            Qiymət / Bahalı", the result "sərfəli / baha"; wave 3 review). */}
        <div className="flex justify-between mt-1.5 text-xs text-gray-400 font-medium">
          <span>{t("result.gauge.cheap")}</span>
          <span>{t("result.gauge.expensive")}</span>
        </div>
      </div>
      )}

      {/* Price Comparison - also suppressed for mortgage-transfer because
          the "asking" number isn't the real asking; the banner spells it out. */}
      {!isNeedsVerification && !isLuxury && !isSuspiciousHigh && (
      // Stack vertically on mobile - the asking + fair-value
      // numbers are wide enough (especially with 6-digit AZN prices
      // and the localised " AZN" / " /ay" suffix in AZ/RU) that
      // splitting the row left them truncating mid-number on phones.
      // Tablet+ keeps the side-by-side comparison.
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 pt-4 border-t border-gray-200/50">
        <div>
          <div className="text-xs text-gray-500 font-medium mb-1">
            {t("result.asking")}
          </div>
          <div className="text-xl font-extrabold text-gray-900">
            {formatPrice(price)}{" "}
            <span className="text-xs font-medium text-gray-400">{unit}</span>
          </div>
        </div>
        <div>
          <div className="mb-1 flex items-center gap-1 text-xs font-medium text-gray-500">
            {t("result.fairzone")}
            {hasBand && zone && (
              <HoverTip text={zoneTip}>
                <Info className="h-3.5 w-3.5 text-gray-400" aria-hidden />
              </HoverTip>
            )}
          </div>
          {/* The fair zone, gray on every tier: a neutral fact, not the
              verdict's colour (the range was painted in the verdict's
              rose on the listing page). */}
          <div className="text-lg font-extrabold tabular-nums text-gray-700 sm:text-xl">
            {formatPrice(roundBand(zoneLow))}
            {" - "}
            {formatPrice(roundBand(zoneHigh))}{" "}
            <span className="text-xs font-medium text-gray-400">{unit}</span>
          </div>
          {hasBand && confidence_pct != null && (
            <div className="text-xs text-gray-400 font-medium mt-0.5">
              {t("result.ai.confidence").replace("{pct}", String(confidence_pct))}
            </div>
          )}
        </div>
      </div>
      )}
      {/* Description-penalty note - rendered OUTSIDE the grid so the
          Russian / Azerbaijani wording (which runs noticeably longer
          than the English) gets the full card width to wrap into.
          Fires only when the LLM's typed findings shifted the fair-
          value band. Full per-finding breakdown lives in the
          PriceFactorsPanel below. */}
      {!isNeedsVerification && !isLuxury && !isSuspiciousHigh &&
        model_estimate_price != null &&
        total_adjustment_pct != null &&
        total_adjustment_pct !== 0 && (
        <div className="text-xs text-amber-700 font-medium leading-snug pt-2 border-t border-amber-100">
          {t("result.adjusted.hint").replace(
            "{price}",
            `${formatPrice(roundEstimate(model_estimate_price, analysis_mode === "rent"))} AZN`,
          )}
        </div>
      )}
    </div>
  );
}
